import * as React from 'react';

import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  FormControlLabel,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import type { UIMessage } from 'ai';
import { MessageSquare, Trash2 } from 'lucide-react';

import { listArtifacts } from '@modules/_shared/components/chat/artifactApi';
import { chatDeleteUrl } from '@modules/_shared/components/chat/artifactFiles';
import {
  chatApiBase,
  chatFetch,
  getChatHeaders,
} from '@modules/_shared/components/chat/chatFetch';
import { formatStrictRelativeTime } from '@modules/_shared/utils/formatting';
import { useLocale } from '@shared/hooks/locale';
import { useTheme } from '@shared/theme/theme';

type ChatSummary = {
  id: string;
  title: string;
  createdAt: string;
  model?: string | null;
};

type ChatHistoryProps = {
  activeChatId: string | null;
  onSelectChat: (
    chatId: string,
    messages: Array<{
      id: string;
      role: 'user' | 'assistant';
      parts: UIMessage['parts'];
      // Streamed message metadata (model/usage/cost) persisted with the row.
      metadata?: unknown;
    }>,
    model?: string | null,
  ) => void;
  onNewChat: () => void;
  onBack: () => void;
};

// Deleting a chat keeps its files (they stay in Files) unless the user opts in
// here. No files, no checkbox: there would be nothing for it to delete.
export const DeleteChatFilesOption = ({
  fileCount,
  checked,
  onChange,
}: {
  fileCount: number;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) => {
  const { t } = useLocale();
  if (fileCount <= 0) {
    return null;
  }
  return (
    <FormControlLabel
      sx={{ mt: 1 }}
      control={
        <Checkbox
          size="small"
          checked={checked}
          onChange={event => onChange(event.target.checked)}
        />
      }
      label={t('Also delete the {{count}} file saved in this chat', {
        count: fileCount,
      })}
    />
  );
};

export const ChatHistory = ({
  activeChatId,
  onSelectChat,
  onNewChat,
  onBack,
}: ChatHistoryProps) => {
  const { t, dateLocale } = useLocale();
  const { colors } = useTheme();
  const [chats, setChats] = React.useState<ChatSummary[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [hasMore, setHasMore] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [loadingChatId, setLoadingChatId] = React.useState<string | null>(null);
  const [deletingChatId, setDeletingChatId] = React.useState<string | null>(
    null,
  );
  const [chatToDelete, setChatToDelete] = React.useState<ChatSummary | null>(
    null,
  );
  // Files saved in chatToDelete, counted when the dialog opens. Stays 0 when
  // the count fails or files are off here: the delete then keeps any files.
  const [deleteFileCount, setDeleteFileCount] = React.useState(0);
  const [deleteFiles, setDeleteFiles] = React.useState(false);
  const fileCountAbortRef = React.useRef<AbortController | null>(null);
  const selectAbortRef = React.useRef<AbortController | null>(null);

  React.useEffect(() => {
    return () => {
      selectAbortRef.current?.abort();
      fileCountAbortRef.current?.abort();
    };
  }, []);

  const fetchChats = React.useCallback(
    async (cursor?: string | null, signal?: AbortSignal) => {
      const params = new URLSearchParams({ limit: '20' });
      if (cursor) {
        params.set('cursor', cursor);
      }

      const res = await chatFetch(
        `${chatApiBase}/history?${params.toString()}`,
        {
          headers: getChatHeaders(),
          signal,
        },
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error ?? t('Failed to load history'));
      }
      return res.json() as Promise<{
        chats: ChatSummary[];
        hasMore: boolean;
      }>;
    },
    [t],
  );

  React.useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchChats(null, controller.signal)
      .then(data => {
        if (controller.signal.aborted) {
          return;
        }
        setChats(data.chats);
        setHasMore(data.hasMore);
      })
      .catch(err => {
        if (controller.signal.aborted) {
          return;
        }
        setError(
          err instanceof Error ? err.message : t('Failed to load chat history'),
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });
    return () => controller.abort();
  }, [fetchChats, t]);

  const handleLoadMore = async () => {
    if (!hasMore || loadingMore || chats.length === 0) {
      return;
    }
    setLoadingMore(true);
    try {
      const lastChat = chats[chats.length - 1];
      const data = await fetchChats(lastChat.id);
      setChats(prev => [...prev, ...data.chats]);
      setHasMore(data.hasMore);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('Failed to load more'));
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSelect = async (chatSummary: ChatSummary) => {
    if (chatSummary.id === activeChatId) {
      onBack();
      return;
    }
    selectAbortRef.current?.abort();
    const controller = new AbortController();
    selectAbortRef.current = controller;
    setLoadingChatId(chatSummary.id);
    try {
      const res = await chatFetch(`${chatApiBase}/${chatSummary.id}`, {
        headers: getChatHeaders(),
        signal: controller.signal,
      });
      if (!res.ok) {
        throw new Error(t('Failed to load chat'));
      }
      const data = await res.json();
      if (!controller.signal.aborted) {
        onSelectChat(
          chatSummary.id,
          data.messages ?? [],
          data.model ?? chatSummary.model ?? null,
        );
      }
    } catch (err) {
      if (err instanceof Error && err.name !== 'AbortError') {
        setError(t('Failed to load chat. Please try again.'));
      }
    } finally {
      if (selectAbortRef.current === controller) {
        setLoadingChatId(null);
      }
    }
  };

  const promptDelete = (e: React.MouseEvent, chatSummary: ChatSummary) => {
    e.stopPropagation();
    setChatToDelete(chatSummary);
    setDeleteFileCount(0);
    setDeleteFiles(false);
    fileCountAbortRef.current?.abort();
    const controller = new AbortController();
    fileCountAbortRef.current = controller;
    listArtifacts({ chatId: chatSummary.id, signal: controller.signal })
      .then(artifacts => {
        if (!controller.signal.aborted) {
          setDeleteFileCount(artifacts.length);
        }
      })
      .catch(() => {
        // No count, no checkbox: the chat delete still works and keeps files.
      });
  };

  const closeDeleteDialog = () => {
    fileCountAbortRef.current?.abort();
    setChatToDelete(null);
  };

  const handleDelete = async () => {
    if (!chatToDelete || deletingChatId === chatToDelete.id) {
      return;
    }
    const target = chatToDelete;
    const url = chatDeleteUrl(chatApiBase, target.id, {
      fileCount: deleteFileCount,
      deleteFiles,
    });
    closeDeleteDialog();
    setDeletingChatId(target.id);
    try {
      const res = await chatFetch(url, {
        method: 'DELETE',
        headers: getChatHeaders(),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error ?? t('Failed to delete chat'));
      }
      setChats(prev => prev.filter(c => c.id !== target.id));
      if (target.id === activeChatId) {
        onNewChat();
      }
    } catch {
      setError(t('Failed to delete chat'));
    } finally {
      setDeletingChatId(null);
    }
  };

  if (loading) {
    return (
      <Stack
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          p: 4,
        }}
      >
        <CircularProgress size={24} />
        <Button onClick={onBack} sx={{ mt: 2 }} variant="text">
          {t('Back')}
        </Button>
      </Stack>
    );
  }

  // Only take over the whole panel when the initial load failed and there is
  // nothing to show. Once chats are loaded, surface follow-up errors inline so
  // a failed "load more" / "open chat" doesn't wipe the visible list.
  if (error && chats.length === 0) {
    return (
      <Stack
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          p: 4,
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" sx={{ color: colors.grey500 }}>
          {error}
        </Typography>
        <Button onClick={onBack} sx={{ mt: 2 }} variant="text">
          {t('Back')}
        </Button>
      </Stack>
    );
  }

  if (chats.length === 0) {
    return (
      <Stack
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          color: colors.grey400,
          textAlign: 'center',
          p: 4,
        }}
      >
        <MessageSquare size={32} />
        <Typography variant="body2" sx={{ mt: 1.5, color: colors.grey500 }}>
          {t('No past conversations')}
        </Typography>
        <Button onClick={onBack} sx={{ mt: 2 }} variant="text">
          {t('Back')}
        </Button>
      </Stack>
    );
  }

  return (
    <div css={{ flex: 1, overflowY: 'auto' }}>
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{ m: 1, borderRadius: 1 }}
        >
          {error}
        </Alert>
      )}
      <List disablePadding>
        {chats.map(chat => {
          const isActive = chat.id === activeChatId;
          const isLoadingThis = loadingChatId === chat.id;
          return (
            <ListItemButton
              key={chat.id}
              selected={isActive}
              onClick={() => handleSelect(chat)}
              sx={{
                py: 1.25,
                px: 1.5,
                gap: 1,
                borderBottom: `1px solid ${colors.divider}`,
              }}
            >
              <ListItemText
                primary={chat.title}
                secondary={formatStrictRelativeTime(chat.createdAt, dateLocale)}
                slotProps={{
                  primary: {
                    noWrap: true,
                    sx: {
                      fontWeight: isActive ? 600 : 400,
                      fontSize: '0.8125rem',
                    },
                  },
                  secondary: {
                    noWrap: true,
                    sx: { fontSize: '0.6875rem' },
                  },
                }}
              />
              {isLoadingThis || deletingChatId === chat.id ? (
                <CircularProgress size={14} />
              ) : (
                <IconButton
                  size="small"
                  onClick={e => promptDelete(e, chat)}
                  title={t('Delete')}
                  aria-label={t('Delete')}
                  sx={{
                    opacity: 0.4,
                    '&:hover': { opacity: 1 },
                    flexShrink: 0,
                  }}
                >
                  <Trash2 size={14} />
                </IconButton>
              )}
            </ListItemButton>
          );
        })}
      </List>
      {hasMore && (
        <Stack sx={{ alignItems: 'center', p: 1.5 }}>
          <Button
            size="small"
            onClick={handleLoadMore}
            disabled={loadingMore}
            variant="text"
            sx={{ fontSize: '0.75rem', textTransform: 'none' }}
          >
            {loadingMore ? t('Loading…') : t('Load more')}
          </Button>
        </Stack>
      )}
      <Dialog open={chatToDelete != null} onClose={closeDeleteDialog}>
        <DialogContent>
          <DialogContentText>
            {t('Are you sure you want to delete this conversation?')}
          </DialogContentText>
          <DeleteChatFilesOption
            fileCount={deleteFileCount}
            checked={deleteFiles}
            onChange={setDeleteFiles}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>{t('Cancel')}</Button>
          <Button
            onClick={handleDelete}
            color="error"
            startIcon={<Trash2 />}
            autoFocus
          >
            {t('Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
